import type { CustomSeriesRenderItem, EChartsOption } from 'echarts'
import * as echarts from 'echarts'

export const DOOR_OPEN_STATE_OPEN = 'Aberta'
export const DOOR_OPEN_STATE_CLOSED = 'Fechada'

/** Padrão visual abertura de porta: vermelho = aberta (0), amarelo = fechada (1). */
export const DOOR_OPEN_STATE_COLORS = {
  [DOOR_OPEN_STATE_OPEN]: '#D9534F',
  [DOOR_OPEN_STATE_CLOSED]: '#ffe167',
} as const

export type DoorOpenStateName = typeof DOOR_OPEN_STATE_OPEN | typeof DOOR_OPEN_STATE_CLOSED

export type DoorOpenTimelinePoint = {
  x: string
  y: [number, number]
}

export type DoorOpenPeriodInput = {
  y: [string | number, string | number]
}

export type DoorOpenSensorInput = {
  name: string
  data: DoorOpenPeriodInput[]
}

export type DoorOpenSegment = {
  state: DoorOpenStateName
  start: number
  end: number
}

export function buildSensorStateSegments(
  periods: DoorOpenPeriodInput[],
  convertTs: (value: string | number) => number,
  rangeStart?: number,
  rangeEnd?: number,
): DoorOpenSegment[] {
  const sorted = [...periods]
    .map((period) => ({
      start: convertTs(period.y[0]),
      end: convertTs(period.y[1]),
    }))
    .filter((period) => Number.isFinite(period.start) && Number.isFinite(period.end) && period.end > period.start)
    .sort((a, b) => a.start - b.start)

  if (sorted.length === 0) {
    if (rangeStart != null && rangeEnd != null && rangeEnd > rangeStart) {
      return [{ state: DOOR_OPEN_STATE_CLOSED, start: rangeStart, end: rangeEnd }]
    }
    return []
  }

  const segments: DoorOpenSegment[] = []
  let cursor = rangeStart ?? sorted[0].start

  for (const { start, end } of sorted) {
    const openStart = Math.max(start, rangeStart ?? start)
    const openEnd = Math.min(end, rangeEnd ?? end)
    if (openEnd <= openStart) continue

    if (openStart > cursor) {
      segments.push({ state: DOOR_OPEN_STATE_CLOSED, start: cursor, end: openStart })
    }

    segments.push({ state: DOOR_OPEN_STATE_OPEN, start: openStart, end: openEnd })
    cursor = openEnd
  }

  if (rangeEnd != null && cursor < rangeEnd) {
    segments.push({ state: DOOR_OPEN_STATE_CLOSED, start: cursor, end: rangeEnd })
  }

  return segments
}

/** Monta séries Apex rangeBar (State Timeline): Aberta / Fechada por sensor. */
export function buildDoorOpenStateTimelineSeries(
  sensors: DoorOpenSensorInput[],
  convertTs: (value: string | number) => number,
  rangeStart?: number,
  rangeEnd?: number,
): { name: string; data: DoorOpenTimelinePoint[] }[] {
  const openData: DoorOpenTimelinePoint[] = []
  const closedData: DoorOpenTimelinePoint[] = []

  for (const sensor of sensors) {
    const segments = buildSensorStateSegments(sensor.data || [], convertTs, rangeStart, rangeEnd)
    for (const segment of segments) {
      const point = { x: sensor.name, y: [segment.start, segment.end] as [number, number] }
      if (segment.state === DOOR_OPEN_STATE_OPEN) openData.push(point)
      else closedData.push(point)
    }
  }

  return [
    { name: DOOR_OPEN_STATE_CLOSED, data: closedData },
    { name: DOOR_OPEN_STATE_OPEN, data: openData },
  ]
}

export function countDoorOpenTimelineRows(sensors: DoorOpenSensorInput[]): number {
  return sensors.length
}

export function resolveDoorOpenTimelineChartHeight(rowCount: number): number {
  if (rowCount <= 0) return 140
  if (rowCount === 1) return 150
  return Math.min(560, 100 + rowCount * 52)
}

export function clampDoorOpenTimelineXAxis(
  rangeMin: number,
  rangeMax: number,
  min: number,
  max: number,
): { min: number; max: number } {
  const fullSpan = rangeMax - rangeMin
  if (fullSpan <= 0) return { min: rangeMin, max: rangeMax }

  const span = max - min
  if (span >= fullSpan) {
    return { min: rangeMin, max: rangeMax }
  }

  let clampedMin = min
  let clampedMax = max

  if (clampedMin < rangeMin) {
    clampedMin = rangeMin
    clampedMax = rangeMin + span
  }
  if (clampedMax > rangeMax) {
    clampedMax = rangeMax
    clampedMin = rangeMax - span
  }

  return { min: clampedMin, max: clampedMax }
}

type ApexChartContext = {
  updateOptions: (options: { xaxis: { min: number; max: number } }, redraw?: boolean, animate?: boolean, updateSyncedCharts?: boolean) => void
}

export function buildDoorOpenTimelineRangeClampEvents(rangeMin: number, rangeMax: number) {
  const enforceClamp = (chartContext: ApexChartContext, min: number, max: number) => {
    const clamped = clampDoorOpenTimelineXAxis(rangeMin, rangeMax, min, max)
    if (clamped.min !== min || clamped.max !== max) {
      chartContext.updateOptions({ xaxis: clamped }, false, false, false)
    }
  }

  return {
    beforeZoom: (_chart: unknown, { xaxis }: { xaxis: { min: number; max: number } }) => ({
      xaxis: clampDoorOpenTimelineXAxis(rangeMin, rangeMax, xaxis.min, xaxis.max),
    }),
    scrolled: (chartContext: ApexChartContext, { xaxis }: { xaxis: { min: number; max: number } }) => {
      enforceClamp(chartContext, xaxis.min, xaxis.max)
    },
    zoomed: (chartContext: ApexChartContext, { xaxis }: { xaxis: { min: number; max: number } }) => {
      enforceClamp(chartContext, xaxis.min, xaxis.max)
    },
  }
}

export function buildDoorOpenStateTimelineChartOptions(params: {
  id: string
  min?: number
  max?: number
  height: number
  rowCount?: number
}) {
  const { id, min, max, height, rowCount = 1 } = params
  const hasRange = min != null && max != null && max > min
  const barHeight = rowCount === 1 ? '48px' : '80%'

  return {
    chart: {
      type: 'rangeBar' as const,
      id,
      toolbar: { show: true },
      height,
      animations: { enabled: false },
      zoom: {
        enabled: true,
        type: 'x' as const,
        autoScaleYaxis: false,
      },
      ...(hasRange ? { events: buildDoorOpenTimelineRangeClampEvents(min, max) } : {}),
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight,
        rangeBarGroupRows: true,
      },
    },
    colors: [DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_CLOSED], DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_OPEN]],
    fill: {
      type: 'solid',
      opacity: 0.9,
    },
    stroke: {
      width: 0,
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: 'datetime' as const,
      min,
      max,
      labels: {
        datetimeUTC: false,
        format: 'dd/MM/yyyy HH:mm',
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px',
        },
      },
    },
    legend: {
      position: 'top' as const,
      horizontalAlign: 'center' as const,
      markers: {
        radius: 0,
      },
    },
    tooltip: {
      custom: ({
        seriesIndex,
        dataPointIndex,
        w,
      }: {
        seriesIndex: number
        dataPointIndex: number
        w: {
          globals: { seriesNames: string[] }
          config: { series: { data: DoorOpenTimelinePoint[] }[] }
        }
      }) => {
        const seriesName = w.globals.seriesNames[seriesIndex] || ''
        const point = w.config.series[seriesIndex]?.data?.[dataPointIndex]
        if (!point) return ''

        const start = new Date(point.y[0]).toLocaleString('pt-BR')
        const end = new Date(point.y[1]).toLocaleString('pt-BR')
        const color = DOOR_OPEN_STATE_COLORS[seriesName as DoorOpenStateName] || '#04304c'

        return `
          <div class="px-3 py-2 text-sm">
            <div class="mb-1 font-semibold" style="color:${color}">${seriesName}</div>
            <div class="text-slate-600">${point.x}</div>
            <div class="mt-1 text-xs text-slate-500">${start} → ${end}</div>
          </div>
        `
      },
    },
    grid: {
      borderColor: '#e2e8f0',
      padding: {
        top: -8,
        bottom: -8,
        left: 8,
        right: 12,
      },
    },
  }
}

type EchartsAlarmPeriod = [number, number]

/** State Timeline em ECharts (detalhe do sensor): faixas Aberta/Fechada + overlay de alarmes. */
export function buildDoorOpenEchartsStateTimelineOption(params: {
  rowLabel: string
  openPeriods: DoorOpenPeriodInput[]
  rangeStart: number
  rangeEnd: number
  alarmPeriods: EchartsAlarmPeriod[]
  hoveredAlarmIndex: number | null
  dataZoom: { start: number; end: number }
}): EChartsOption {
  const {
    rowLabel,
    openPeriods,
    rangeStart,
    rangeEnd,
    alarmPeriods,
    hoveredAlarmIndex,
    dataZoom,
  } = params

  const segments = buildSensorStateSegments(openPeriods, (value) => Number(value), rangeStart, rangeEnd)

  const statusData = segments.map((segment) => ({
    name: segment.state,
    value: [0, segment.start, segment.end, segment.state === DOOR_OPEN_STATE_OPEN ? 0 : 1],
    itemStyle: {
      color: DOOR_OPEN_STATE_COLORS[segment.state],
    },
  }))

  const alarmData = alarmPeriods.map(([start, end], index) => {
    const isHighlighted = index === hoveredAlarmIndex
    return {
      name: 'Alarme',
      value: [0, start, end],
      itemStyle: {
        color: isHighlighted ? 'rgba(251, 84, 84, 0.35)' : 'rgba(251, 84, 84, 0.18)',
        borderColor: isHighlighted ? '#BF1F1F' : '#FB5454',
        borderWidth: isHighlighted ? 2 : 1,
      },
    }
  })

  const MIN_SEGMENT_WIDTH = 2

  const renderTimelineItem: CustomSeriesRenderItem = (params, api) => {
    const coordSys = params.coordSys as typeof params.coordSys & {
      x: number
      y: number
      width: number
      height: number
    }
    const categoryIndex = api.value(0)
    const start = api.coord([api.value(1), categoryIndex])
    const end = api.coord([api.value(2), categoryIndex])
    const height = api.size([0, 1])[1] * 0.55
    const width = Math.max(end[0] - start[0], MIN_SEGMENT_WIDTH)
    const rectShape = echarts.graphic.clipRectByRect(
      {
        x: start[0],
        y: start[1] - height / 2,
        width,
        height,
      },
      {
        x: coordSys.x,
        y: coordSys.y,
        width: coordSys.width,
        height: coordSys.height,
      },
    )

    if (!rectShape) return undefined

    return {
      type: 'rect' as const,
      transition: ['shape'],
      shape: rectShape,
      style: api.style(),
    }
  }

  return {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const value = params?.value
        if (!Array.isArray(value) || value.length < 3) return ''
        const start = new Date(value[1]).toLocaleString('pt-BR')
        const end = new Date(value[2]).toLocaleString('pt-BR')
        const name = params.name || ''
        const color =
          name === DOOR_OPEN_STATE_OPEN || name === DOOR_OPEN_STATE_CLOSED
            ? DOOR_OPEN_STATE_COLORS[name as DoorOpenStateName]
            : '#FB5454'
        return `
          <div style="font-weight:600;color:${color};margin-bottom:4px">${name}</div>
          <div>${rowLabel}</div>
          <div style="font-size:12px;color:#64748B;margin-top:4px">${start} → ${end}</div>
        `
      },
    },
    legend: {
      data: [
        {
          name: DOOR_OPEN_STATE_CLOSED,
          itemStyle: { color: DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_CLOSED] },
        },
        {
          name: DOOR_OPEN_STATE_OPEN,
          itemStyle: { color: DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_OPEN] },
        },
      ],
      top: 0,
      left: 0,
      textStyle: { fontSize: 10 },
    },
    grid: {
      left: '2%',
      right: '2%',
      bottom: '22%',
      top: '16%',
      containLabel: true,
    },
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: dataZoom.start,
        end: dataZoom.end,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        preventDefaultMouseMove: true,
        filterMode: 'none',
      },
      {
        type: 'slider',
        xAxisIndex: 0,
        start: dataZoom.start,
        end: dataZoom.end,
        height: 20,
        bottom: 28,
        borderColor: 'transparent',
        fillerColor: 'rgba(234, 179, 8, 0.25)',
        handleStyle: { color: '#EAB308' },
        dataBackground: { lineStyle: { opacity: 0.3 }, areaStyle: { opacity: 0.05 } },
        moveHandleSize: 6,
        textStyle: { fontSize: 10 },
        filterMode: 'none',
      },
    ],
    xAxis: {
      type: 'time',
      min: rangeStart,
      max: rangeEnd,
      boundaryGap: [0, 0] as [string | number, string | number],
      axisLabel: {
        fontSize: 10,
        formatter: (value: number) => {
          const d = new Date(value)
          return d.toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        },
      },
    },
    yAxis: {
      type: 'category',
      data: [rowLabel],
      axisLabel: { fontSize: 11 },
      splitLine: { show: false },
    },
    series: [
      {
        name: DOOR_OPEN_STATE_CLOSED,
        type: 'custom',
        renderItem: renderTimelineItem,
        encode: { x: [1, 2], y: 0 },
        data: statusData.filter((item) => item.name === DOOR_OPEN_STATE_CLOSED),
        clip: false,
      },
      {
        name: DOOR_OPEN_STATE_OPEN,
        type: 'custom',
        renderItem: renderTimelineItem,
        encode: { x: [1, 2], y: 0 },
        data: statusData.filter((item) => item.name === DOOR_OPEN_STATE_OPEN),
        clip: false,
      },
      {
        name: 'Alarme',
        type: 'custom',
        renderItem: renderTimelineItem,
        encode: { x: [1, 2], y: 0 },
        data: alarmData,
        clip: true,
        z: 10,
        silent: false,
      },
    ],
  }
}

export type DoorOpenMultiRowInput = {
  /** Nome do sensor (linha do eixo Y). */
  label: string
  openPeriods: DoorOpenPeriodInput[]
}

/**
 * State Timeline multi-linha em ECharts: uma linha por sensor de porta,
 * com a faixa de alarme replicada em todas as linhas.
 */
export function buildMultiRowDoorOpenEchartsStateTimelineOption(params: {
  rows: DoorOpenMultiRowInput[]
  rangeStart: number
  rangeEnd: number
  alarmPeriods: EchartsAlarmPeriod[]
  dataZoom: { start: number; end: number }
}): EChartsOption {
  const { rows, rangeStart, rangeEnd, alarmPeriods, dataZoom } = params

  const statusData: Array<{ name: DoorOpenStateName; value: [number, number, number, number]; itemStyle: { color: string } }> = []
  rows.forEach((row, rowIndex) => {
    const segments = buildSensorStateSegments(row.openPeriods, (value) => Number(value), rangeStart, rangeEnd)
    for (const segment of segments) {
      statusData.push({
        name: segment.state,
        value: [rowIndex, segment.start, segment.end, segment.state === DOOR_OPEN_STATE_OPEN ? 0 : 1],
        itemStyle: { color: DOOR_OPEN_STATE_COLORS[segment.state] },
      })
    }
  })

  const alarmData: Array<{ name: string; value: [number, number, number]; itemStyle: Record<string, unknown> }> = []
  rows.forEach((_row, rowIndex) => {
    for (const [start, end] of alarmPeriods) {
      alarmData.push({
        name: 'Alarme',
        value: [rowIndex, start, end],
        itemStyle: {
          color: 'rgba(251, 84, 84, 0.18)',
          borderColor: '#FB5454',
          borderWidth: 1,
        },
      })
    }
  })

  const MIN_SEGMENT_WIDTH = 2

  const renderTimelineItem: CustomSeriesRenderItem = (params, api) => {
    const coordSys = params.coordSys as typeof params.coordSys & {
      x: number
      y: number
      width: number
      height: number
    }
    const categoryIndex = api.value(0)
    const start = api.coord([api.value(1), categoryIndex])
    const end = api.coord([api.value(2), categoryIndex])
    const height = api.size([0, 1])[1] * 0.55
    const width = Math.max(end[0] - start[0], MIN_SEGMENT_WIDTH)
    const rectShape = echarts.graphic.clipRectByRect(
      {
        x: start[0],
        y: start[1] - height / 2,
        width,
        height,
      },
      {
        x: coordSys.x,
        y: coordSys.y,
        width: coordSys.width,
        height: coordSys.height,
      },
    )

    if (!rectShape) return undefined

    return {
      type: 'rect' as const,
      transition: ['shape'],
      shape: rectShape,
      style: api.style(),
    }
  }

  return {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const value = params?.value
        if (!Array.isArray(value) || value.length < 3) return ''
        const rowIndex = Number(value[0])
        const rowLabel = rows[rowIndex]?.label ?? ''
        const start = new Date(value[1]).toLocaleString('pt-BR')
        const end = new Date(value[2]).toLocaleString('pt-BR')
        const name = params.name || ''
        const color =
          name === DOOR_OPEN_STATE_OPEN || name === DOOR_OPEN_STATE_CLOSED
            ? DOOR_OPEN_STATE_COLORS[name as DoorOpenStateName]
            : '#FB5454'
        return `
          <div style="font-weight:600;color:${color};margin-bottom:4px">${name}</div>
          <div>${rowLabel}</div>
          <div style="font-size:12px;color:#64748B;margin-top:4px">${start} → ${end}</div>
        `
      },
    },
    legend: {
      data: [
        {
          name: DOOR_OPEN_STATE_CLOSED,
          itemStyle: { color: DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_CLOSED] },
        },
        {
          name: DOOR_OPEN_STATE_OPEN,
          itemStyle: { color: DOOR_OPEN_STATE_COLORS[DOOR_OPEN_STATE_OPEN] },
        },
      ],
      top: 0,
      left: 0,
      textStyle: { fontSize: 10 },
    },
    grid: {
      left: '2%',
      right: '2%',
      bottom: '22%',
      top: '16%',
      containLabel: true,
    },
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: dataZoom.start,
        end: dataZoom.end,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        preventDefaultMouseMove: true,
        filterMode: 'none',
      },
      {
        type: 'slider',
        xAxisIndex: 0,
        start: dataZoom.start,
        end: dataZoom.end,
        height: 20,
        bottom: 28,
        borderColor: 'transparent',
        fillerColor: 'rgba(234, 179, 8, 0.25)',
        handleStyle: { color: '#EAB308' },
        dataBackground: { lineStyle: { opacity: 0.3 }, areaStyle: { opacity: 0.05 } },
        moveHandleSize: 6,
        textStyle: { fontSize: 10 },
        filterMode: 'none',
      },
    ],
    xAxis: {
      type: 'time',
      min: rangeStart,
      max: rangeEnd,
      boundaryGap: [0, 0] as [string | number, string | number],
      axisLabel: {
        fontSize: 10,
        formatter: (value: number) => {
          const d = new Date(value)
          return d.toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        },
      },
    },
    yAxis: {
      type: 'category',
      data: rows.map((row) => row.label),
      axisLabel: { fontSize: 11 },
      splitLine: { show: false },
    },
    series: [
      {
        name: DOOR_OPEN_STATE_CLOSED,
        type: 'custom',
        renderItem: renderTimelineItem,
        encode: { x: [1, 2], y: 0 },
        data: statusData.filter((item) => item.name === DOOR_OPEN_STATE_CLOSED),
        clip: false,
      },
      {
        name: DOOR_OPEN_STATE_OPEN,
        type: 'custom',
        renderItem: renderTimelineItem,
        encode: { x: [1, 2], y: 0 },
        data: statusData.filter((item) => item.name === DOOR_OPEN_STATE_OPEN),
        clip: false,
      },
      {
        name: 'Alarme',
        type: 'custom',
        renderItem: renderTimelineItem,
        encode: { x: [1, 2], y: 0 },
        data: alarmData,
        clip: true,
        z: 10,
        silent: false,
      },
    ],
  }
}
